import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {HideableValue, RichTextContent} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    asArray,
    evaluateFunction,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    hideableValueIsVisibleAndNotEqualValue,
    hideableValueModifyValue,
    immerProduce,
    iterableMapToArray,
    objectOmit,
    OverridableValue,
    overridableValueCreateOriginal,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    overridableValueIsOriginal,
    stringIsNotNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import type {DoppeActionTypeService} from '../doppe-action-type-service/doppe-action-type-service';
import {
    convertDoppeWixBookingServiceToServiceSettings,
    doppeActionTypeServiceActionContentSettingsMetadata
} from '../doppe-action-type-service/doppe-action-type-service';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    doppeBusinessEntityDtoIsNotFound,
    DoppeDtoBookingService,
    doppeLinkSpecCreateEntity,
    DoppeLinkSpecEntityType,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemGetDefaultListItemSettingsMigration,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionPartialSettings,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import {
    DoppeExternalServiceProvider,
    doppeExternalServiceProviderResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-external-services';
import type {
    DoppeServicesExternalDataSource
} from '../../client-server-common/types/doppe-services-external-data-source';
import {
    doppeServicesExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common/types/doppe-services-external-data-source';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {bymoPageContextPropsResolveBusinessEntity} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeServicesList {

    export interface ServiceSettings extends
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeService.ServiceSettings {
        serviceTitle: OverridableValue<string>;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ServiceSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeServicesExternalDataSource>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        itemShowPrice: boolean;
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowCoupon: boolean;
        previewShowPrice: boolean;
    }

    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        services?: ServiceSettings[];
        previewServiceButtonText?: string;
    }

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        serviceButtonText?: HideableValue<string>;
    }

    export type PartialProps = DoppeListActionPartialSettings<ActionSettings, ServiceSettings, DeprecatedFields, DeprecatedItemFields>;
}


export const doppeActionTypeServicesList = doppeActionTypeDefineMetadata<DoppeActionTypeServicesList.ActionSettings, DoppeActionTypeServicesList.PartialProps>({
    id: 'io.bymo.action/services-list',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            itemShowPrice = true,
            previewShowDescription = true,
            previewShowTitle = true,
            previewShowCoupon = true,
            previewShowPrice = true,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            itemShowPrice: itemShowPrice,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            previewShowCoupon: previewShowCoupon,
            previewShowPrice: previewShowPrice,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeServicesList.ServiceSettings>({
                ...action.settings,
                listDataItems: listDataItems.map(service => {

                    const {
                        serviceButtonText,
                        ctaButton,
                        serviceTitle = '',
                        servicePageLink = '',
                        description = hideableValueCreateHidden(''),
                        serviceImages = null,
                        servicePeriod = hideableValueCreateHidden(''),
                        serviceSchedule = hideableValueCreateHidden(''),
                        serviceParticipants = hideableValueCreateHidden(''),
                        location = hideableValueCreateHidden(''),
                        couponCode = hideableValueCreateHidden(''),
                        serviceProviderId = null,
                        serviceProvider = null,
                        ribbon = hideableValueCreateHidden(''),
                        priceData = hideableValueCreateHidden({actualPrice: '', discountPrice: ''}),
                        ...restServiceSettings
                    } = service;

                    return {
                        ...restServiceSettings,
                        ...doppeListActionListItemGetDefaultListItemSettingsMigration(service.id),
                        serviceTitle: serviceTitle,
                        servicePageLink: servicePageLink,
                        description: description,
                        serviceImages: serviceImages,
                        servicePeriod: servicePeriod,
                        serviceSchedule: serviceSchedule,
                        serviceParticipants: serviceParticipants,
                        location: location,
                        couponCode: couponCode,
                        serviceProviderId: serviceProviderId,
                        serviceProvider: serviceProvider,
                        ribbon: ribbon,
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: serviceButtonText,
                            showButton: true
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: true,
                            ctaButton: ctaButton,
                        }),
                        ...doppeListActionListItemWithCtaButtonsMigration(service, true),
                        ...doppeListActionListItemWithSectionsMigration(service),
                        priceData: hideableValueModifyValue(priceData, overridableValue => {
                            const innerValue = overridableValueGetValue(overridableValue);
                            const resolved = {...innerValue, discountPrice: innerValue.discountPrice ?? ''};
                            return overridableValueIsOriginal(overridableValue)
                                ? overridableValueCreateOriginal(resolved)
                                : resolved;
                        }),
                    };
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({
                defaultTitle: ''
            }, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeServicesList.ActionSettings;
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeServiceActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                location: true,
                priceData: true,
                ribbon: true,
                serviceParticipants: true,
                servicePeriod: true,
                serviceSchedule: true
            }
    },
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(item => {
            const serviceImages = overridableValueGetValue(item.serviceImages);
            return serviceImages ? asArray(serviceImages) : [];
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const firstItem = actionSettings.listDataItems[0];
        const bookingService = firstItem?.serviceProvider && firstItem?.serviceProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalServiceProviderResolveBusinessEntityReference(firstItem.serviceProvider!, firstItem.serviceProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBookingService : null;
        })() : null;

        const firstItemImages = overridableValueGetOverriddenValue(
            firstItem?.serviceImages,
            bookingService?.media ?? null
        );
        const serviceImage = firstItemImages ? asArray(firstItemImages)[0] ?? null : null;
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, serviceImage);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeServicesExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (plan, skip) => {
                return plan.serviceProvider !== null && plan.serviceProviderId !== null ?
                    doppeExternalServiceProviderResolveBusinessEntityReference(
                        plan.serviceProvider,
                        plan.serviceProviderId
                    ) : skip
            });
        }
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveCouponDataFromListItem: listItem => {
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(listItem.couponCode, ''),
            couponCode: hideableValueGetVisibleValue(listItem.couponCode, ''),
            couponDescription: '',
            couponTitle: listItem.title ?? '',
        }
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            const bookingService = item.serviceProvider && item.serviceProviderId ? (() => {
                const entityDto = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalServiceProviderResolveBusinessEntityReference(item.serviceProvider!, item.serviceProviderId!)
                );
                return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBookingService : null;
            })() : null;

            const resolvedServiceImages = overridableValueGetOverriddenValue(
                item.serviceImages,
                bookingService?.media ?? null
            );

            const sourceLinkSpec = evaluateFunction(() => {
                if (item.serviceProvider === DoppeExternalServiceProvider.Hopp && item.serviceProviderId !== null) {
                    return doppeLinkSpecCreateEntity(DoppeLinkSpecEntityType.Service, item.serviceProviderId);
                }
                return stringIsNotNullableOrWhiteSpace(item.servicePageLink) ? item.servicePageLink : null;
            });

            return {
                title: overridableValueGetOverriddenValue(item.serviceTitle, bookingService?.name ?? ''),
                action: action,
                couponCode: item.couponCode,
                description: hideableValueModifyValue(
                    item.description,
                    value => overridableValueGetOverriddenValue(value, bookingService?.description ?? '')
                ) as HideableValue<RichTextContent>,
                image: resolvedServiceImages ? asArray(resolvedServiceImages)[0] : null,
                itemId: item.id,
                ribbon: hideableValueModifyValue(
                    item.ribbon,
                    value => overridableValueGetOverriddenValue(value, '')
                ) as HideableValue<string>,
                url: doppeActionCtaButtonsResolveMainURL(actionSettings, {
                    sourceLinkSpec: sourceLinkSpec,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps
                }) ?? null,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const service of actionSettings.listDataItems) {
                const serviceImages = overridableValueGetValue(service.serviceImages);
                if (serviceImages) {
                    const images = asArray(serviceImages);
                    service.serviceImages = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    })
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});

export function populateServicesActionServiceSettingsDefaultTexts(options: {
    service: DoppeActionTypeServicesList.ServiceSettings,
    pageContentLocales: DoppeAppPageContentLocales,
}): DoppeActionTypeServicesList.ServiceSettings {

    const {
        pageContentLocales,
        service,
    } = options;

    return {
        ...service,
        serviceTitle: overridableValueGetValue(service.serviceTitle) !== '' ? service.serviceTitle : pageContentLocales["service.actionSettings.title"],
        description: hideableValueModifyValue(service.description, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.description"];
        }),
        couponCode: hideableValueModifyValue(service.couponCode, (value) => {
            return value !== '' ? value : pageContentLocales["service.actionSettings.couponCode"];
        }),
        ribbon: hideableValueModifyValue(service.ribbon, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.ribbon"];
        }),
        location: hideableValueModifyValue(service.location, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.location"];
        }),
        serviceParticipants: hideableValueModifyValue(service.serviceParticipants, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.serviceParticipants"];
        }),
        serviceSchedule: hideableValueModifyValue(service.serviceSchedule, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.serviceSchedule"];
        }),
        servicePeriod: hideableValueModifyValue(service.servicePeriod, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value !== '' ? overridableValue : pageContentLocales["service.actionSettings.servicePeriod"];
        }),
        priceData: hideableValueModifyValue(service.priceData, overridableValue => {
            const value = overridableValueGetValue(overridableValue);
            return value.actualPrice !== '' ? overridableValue : {actualPrice: pageContentLocales["service.actionSettings.price"], discountPrice: ''};
        }),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
    }
}

export function convertDoppeWixServiceToServicesSettings(
    service: DoppeDtoBookingService,
    serviceProvider?: DoppeExternalServiceProvider,
): DoppeActionTypeServicesList.ServiceSettings {

    return {
        ...objectOmit(convertDoppeWixBookingServiceToServiceSettings(service, undefined, undefined, serviceProvider), ['sections', 'ctaButtons']),
        ...doppeListActionListItemGetDefaultListItemSettingsMigration(service.id),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
    }
}